/**
 * Estudo do consórcio: tudo o que o PDF "Por que consórcio" mostra, calculado
 * de uma vez a partir da proposta e das premissas da caixinha.
 *
 * Decisão do Rick (16/10): estes números NÃO aparecem no simulador — só neste
 * PDF, que é a peça de apresentação para o cliente. O simulador continua com
 * os cards de cenário de sempre.
 *
 * A proposta gerada no simulador traz o resultado de computeSimulacao inteiro
 * (o payload espalha `...calculos`). Daqui se refaz a simulação no mês de
 * contemplação escolhido na caixinha, para resumo, parcelas e patrimônio
 * baterem com a venda da carta. Proposta sem esses campos (North IA, registros
 * antigos) ainda gera as páginas de financiamento e venda da carta.
 *
 * Usado pelo web (caixinha, PDF e link) e pela North IA (chat-ai/apresentacao.ts):
 * o mesmo código, para a IA explicar e gravar exatamente o que o botão grava.
 */
import {
  jurosAcumulados,
  montarComparativo,
  valoresDaVenda,
  type Comparativo,
  type EntradaComparativo,
  type Segmento,
  type Taxas,
} from './comparativo.ts';
import { entradaDoPayload, type OpcoesComparativo } from './entrada.ts';
import { calcularEmpate, horizonteDoGrafico, prazoDeEmpate, type Empate } from './empate.ts';
import { simularCreditoAplicado, simularImovelEAluguel, type CreditoAplicado, type ImovelEAluguel } from './patrimonio.ts';
import {
  cenarioPadrao,
  montarResumoCliente,
  parcelasDoPlano,
  type CenarioResumo,
  type ContextoResumo,
  type ResumoCliente,
} from './resumoCliente.ts';
import { computeSimulacao, type SimulacaoResult } from './simulacao.ts';
import type { Alternativa, PayloadProposta, SecaoId } from './tipos.ts';

export const CENARIOS: CenarioResumo[] = ['sorteio', 'lanceFixo', 'lanceFidelidade', 'lanceLivre'];

export type FontesEstudo = {
  juros: string;
  cdi: string;
  reajuste: string;
  agio: string;
  valorizacao: string;
  aluguel: string;
};

export type OpcoesEstudo = OpcoesComparativo & {
  cdiAa: number;
  reajusteAa: number;
  valorizacaoAa: number;
  aluguelAm: number;
  cenario: CenarioResumo;
  fontes: FontesEstudo;
  /** Planos com crédito parecido que o vendedor escolheu mostrar (página de empate). */
  alternativas?: Alternativa[];
  /** Seções que o vendedor desligou; o PDF e o link saem sem elas. */
  secoesOcultas?: SecaoId[];
};

export type Estudo = {
  cliente: string | null;
  segmento: Segmento;
  entrada: EntradaComparativo;
  taxas: Taxas;
  comparativo: Comparativo;
  /** Custo acumulado mês a mês: taxa de administração × juros de SAC e Price. */
  custo: { consorcio: number[]; sac: number[]; price: number[] };
  /**
   * Empate com o financiamento do segmento (SAC no imóvel, Price no veículo) e
   * a curva do outro sistema para o gráfico. Opcional: links gerados antes de
   * 17/10 não têm.
   */
  empate?: { principal: Empate; outro: Empate };
  /** Mês a mês até o fim do prazo: pago, recebido vendendo a carta e saldo do CDB. */
  venda: { pago: number[]; carta: number[]; cdb: number[] };
  /** null quando a proposta não traz a simulação completa. */
  resumo: ResumoCliente | null;
  parcelas: { hoje: Record<CenarioResumo, number[]>; comReajuste: Record<CenarioResumo, number[]> } | null;
  patrimonio: {
    credito: number;
    dinheiroNovoAteContemplar: number;
    aplicado: CreditoAplicado;
    imovel: ImovelEAluguel | null;
  } | null;
  opcoes: OpcoesEstudo;
  /** Quando o estudo foi calculado (ISO). O link mostra esta data, não a de quem abre. */
  geradoEm?: string;
};

const num = (v: unknown): number | null => {
  const n = typeof v === 'number' ? v : Number(v);
  return Number.isFinite(n) ? n : null;
};

/**
 * Refaz computeSimulacao a partir do payload, no mês pedido. Devolve null se o
 * payload não tem as entradas (proposta da North IA, registro antigo).
 *
 * O payload não guarda a taxa de seguro nem o tipo de seguro do grupo, mas
 * guarda o resultado: a taxa sai de seguro ÷ saldo do sorteio, e o seguro pós
 * obrigatório aparece como seguro > 0 com "Seguro: Não".
 */
export const simulacaoDoPayload = (payload: PayloadProposta, mes: number): SimulacaoResult | null => {
  const creditoJunto = num(payload.valorCreditoJunto ?? payload.valorCredito);
  const parcelaSemSeguro = num(payload.parcelaSemSeguro);
  const prazo = num(payload.prazo);
  const taxaAdmin = num(payload.taxaAdmin);
  const saldoSorteio = num(payload.saldoDevedorSorteio);
  if (!creditoJunto || !parcelaSemSeguro || !prazo || !taxaAdmin || saldoSorteio === null) return null;

  const cotas = parseInt(String(payload.juncaoCota ?? '1'), 10) || 1;
  const seguroSorteio = num(payload.seguroSorteio) ?? 0;
  const comSeguro = String(payload.seguro ?? '').toLowerCase() === 'sim';
  const desconto = num(payload.descontoFlex) ?? 0;
  return computeSimulacao({
    valorCredito: creditoJunto / cotas,
    parcelaSemSeguro,
    valorSeguro: num(payload.valorSeguro) ?? 0,
    prazo,
    taxaAdmin,
    mesesContemplacao: mes,
    juncaoCota: String(cotas),
    lanceLivre: String(num(payload.lanceLivre) ?? 0),
    lanceEmbutido: String(num(payload.lanceEmbutido) ?? 0),
    fixedLancePercent: num(payload.lanceFixoPercent) ?? 0,
    percLanceFidelidade: num(payload.lanceFidelidadePercent) ?? 0,
    descontoFlex: String(desconto),
    isFlex: desconto > 0,
    isAuto: false,
    seguroEfetivo: comSeguro ? 'Sim' : 'Não',
    seguroForcadoPos: !comSeguro && seguroSorteio > 0,
    taxaSeguro: saldoSorteio > 0 && seguroSorteio > 0 ? (seguroSorteio / saldoSorteio) * 100 : 0,
  });
};

/** Cenário inicial da caixinha: o lance que o vendedor marcou na proposta. */
export const cenarioDoPayload = (payload: PayloadProposta): CenarioResumo =>
  cenarioPadrao({
    lanceLivreAtivo: payload.lanceLivreAtivo === true,
    lanceFixoAtivo: payload.lanceFixoAtivo === true,
    lanceFidelidadeAtivo: payload.lanceFidelidadeAtivo === true,
  });

export const montarEstudo = (payload: PayloadProposta, opcoes: OpcoesEstudo): Estudo | null => {
  const base = entradaDoPayload(payload, opcoes.segmento);
  if (!base) return null;
  const mes = Math.min(Math.max(1, Math.round(opcoes.mesContemplacao)), base.prazo);
  const entrada: EntradaComparativo = { ...base, mesContemplacao: mes };
  const taxas: Taxas = {
    jurosFinanciamentoAa: opcoes.jurosAa ?? 0,
    cdiAa: opcoes.cdiAa,
    reajusteAa: opcoes.reajusteAa,
    agioPct: opcoes.agioPct,
  };
  const comparativo = montarComparativo(entrada, taxas);

  const custoMensalTaxa = (entrada.credito * entrada.taxaAdminPct) / 100 / entrada.prazo;
  const custo = {
    consorcio: Array.from({ length: entrada.prazo }, (_, k) => custoMensalTaxa * (k + 1)),
    sac: jurosAcumulados(entrada.credito, entrada.prazo, taxas.jurosFinanciamentoAa, 'SAC'),
    price: jurosAcumulados(entrada.credito, entrada.prazo, taxas.jurosFinanciamentoAa, 'Price'),
  };

  const sistema = comparativo.financiamento.financiamento.sistema;
  const ate = horizonteDoGrafico(
    Math.max(entrada.prazo, ...(opcoes.alternativas ?? []).map((a) => a.prazo)),
    prazoDeEmpate(entrada.taxaAdminPct, taxas.jurosFinanciamentoAa, sistema),
  );
  const doSistema = (s: 'SAC' | 'Price') =>
    calcularEmpate({ prazo: entrada.prazo, taxaAdminPct: entrada.taxaAdminPct, jurosAa: taxas.jurosFinanciamentoAa, sistema: s, ate });
  const empate = { principal: doSistema(sistema), outro: doSistema(sistema === 'SAC' ? 'Price' : 'SAC') };

  const venda = { pago: [] as number[], carta: [] as number[], cdb: [] as number[] };
  for (let m = 1; m <= entrada.prazo; m++) {
    const v = valoresDaVenda(entrada, taxas, m);
    venda.pago.push(v.totalPago);
    venda.carta.push(v.recebidoLiquido);
    venda.cdb.push(v.cdbLiquido);
  }

  const sim = simulacaoDoPayload(payload, mes);
  let resumo: ResumoCliente | null = null;
  let parcelas: Estudo['parcelas'] = null;
  let patrimonio: Estudo['patrimonio'] = null;
  if (sim) {
    const ctx: ContextoResumo = {
      prazo: entrada.prazo,
      taxaAdminPct: entrada.taxaAdminPct,
      mesContemplacao: mes,
      reajusteAaPct: opcoes.reajusteAa,
    };
    resumo = montarResumoCliente(sim, ctx, opcoes.cenario);
    const de = (comReajuste: boolean) =>
      Object.fromEntries(CENARIOS.map((c) => [c, parcelasDoPlano(sim, ctx, c, comReajuste)])) as Record<
        CenarioResumo,
        number[]
      >;
    parcelas = { hoje: de(false), comReajuste: de(true) };
    const e = {
      credito: resumo.comReajuste.creditoLiquido,
      dinheiroNovoAteContemplar: resumo.comReajuste.dinheiroNovoAteContemplar,
      parcelasDepois: parcelas.comReajuste[opcoes.cenario].slice(resumo.parcelasAntes),
    };
    patrimonio = {
      credito: e.credito,
      dinheiroNovoAteContemplar: e.dinheiroNovoAteContemplar,
      aplicado: simularCreditoAplicado(e, opcoes.cdiAa),
      imovel:
        opcoes.segmento === 'imovel'
          ? simularImovelEAluguel(e, { valorizacaoAa: opcoes.valorizacaoAa, aluguelAm: opcoes.aluguelAm })
          : null,
    };
  }

  return {
    cliente: typeof payload.clientName === 'string' && payload.clientName.trim() ? payload.clientName.trim() : null,
    segmento: opcoes.segmento,
    entrada,
    taxas,
    comparativo,
    custo,
    empate,
    venda,
    resumo,
    parcelas,
    patrimonio,
    opcoes,
    geradoEm: new Date().toISOString(),
  };
};
